import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { History, RotateCcw, User as UserIcon, Calendar } from 'lucide-react';
import { useT } from '@/store/i18n';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { confirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from '@/components/ui/Toast';
import { api } from '@/lib/api';

interface Revision {
  id: number;
  question_id: number;
  version: number;
  changed_by_name?: string;
  change_reason?: string;
  created_at: string;
}

interface Props {
  questionId: number | null;
  onClose: () => void;
}

export function RevisionHistoryModal({ questionId, onClose }: Props) {
  const t = useT();
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: ['revisions', questionId],
    queryFn: async () => {
      const r = await api<{ data?: Revision[] | { revisions?: Revision[] } }>(`revisions/index.php?question_id=${questionId}`);
      const d = r.data;
      if (Array.isArray(d)) return d;
      return (d as { revisions?: Revision[] } | undefined)?.revisions ?? [];
    },
    enabled: !!questionId,
  });

  const restore = useMutation({
    mutationFn: (revisionId: number) =>
      api('revisions/restore.php', { method: 'POST', json: { revision_id: revisionId, question_id: questionId } }),
    onSuccess: () => {
      toast.success(t('revisions.success'));
      qc.invalidateQueries({ queryKey: ['questions'] });
      qc.invalidateQueries({ queryKey: ['question-detail', questionId] });
      qc.invalidateQueries({ queryKey: ['revisions', questionId] });
      onClose();
    },
    onError: (e: Error) => toast.error(t('revisions.fail'), e.message),
  });

  return (
    <Modal
      open={!!questionId}
      onClose={onClose}
      title={t('revisions.title')}
      description={t('revisions.desc')}
      size="md"
    >
      {list.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      ) : (list.data ?? []).length === 0 ? (
        <EmptyState
          icon={<History className="w-7 h-7" />}
          title={t('revisions.empty')}
          description={t('revisions.empty_desc')}
        />
      ) : (
        <ul className="divide-y divide-slate-100 -mx-6 px-6">
          {(list.data ?? []).map((r) => (
            <li key={r.id} className="py-3 flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-violet-100 text-violet-700 flex items-center justify-center flex-shrink-0">
                <History className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge tone="brand">{t('revisions.version')} {r.version}</Badge>
                  {r.change_reason && (
                    <span className="text-xs text-slate-600">{r.change_reason}</span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1">
                    <UserIcon className="w-3 h-3" />
                    {r.changed_by_name ?? '—'}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(r.created_at).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' })}
                  </span>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<RotateCcw className="w-3.5 h-3.5" />}
                onClick={async () => {
                  const ok = await confirmDialog({
                    title: `${t('revisions.restore_title')} ${r.version}?`,
                    description: t('revisions.restore_desc'),
                    confirmLabel: t('revisions.restore'),
                  });
                  if (ok) restore.mutate(r.id);
                }}
              >
                {t('revisions.restore')}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
